import React, { useState, useRef } from 'react';
import { Formik } from 'formik';
import { PropTypes } from 'prop-types';
import { get } from 'lodash';
import { Modal, Row, Grid, Text, Spinner } from '@custom-components';
import { ConnectorEntityContainer } from '@containers-admin/Connector/Entity';
import NewConnection from './NewConnection';
import Initiate from './NewConnection/StepsPage/Initiate';
import { Content } from './CreateConnectionsModal.styles';

function CreateConnectionsModal(props) {
  const { closeModal, router, /* admin, */ createConnectorConnection } = props;
  const routedPropertyName = get(router, 'query.propertyName', '');
  const [searchInput, setSearchInput] = useState({
    propertyName: { value: routedPropertyName, query: routedPropertyName }
  });
  const [mainTitle, setMainTitle] = useState('What would you like to create a connection for?');
  const [stepId, setStepId] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSmallModal, setOpenSmallModal] = useState(false);
  const selectedOption = useRef([]);
  const totalAttributes = useRef(0);
  let optionHasChanged = false;

  /** Capitalize the first letter of each word from a string */
  const hydrateConnectionOptions = (options) => {
    if (Array.isArray(options) && options.length) {
      return options.map(element => {
        const key = 'name' in element ? 'name' : 'connectionType'; // Key returned on GQL query
        const newElement = { ...element };

        if (element[key]) {
          // Replace all occurrences using regex and split it into array of words
          const words = element[key].replace(/_/g, ' ').split(' ');

          words.forEach(([first, ...rest], i) => {
            words[i] = first.toUpperCase() + rest.join('').toLowerCase();
          });

          const transformedText = words.join(' ');
          newElement.label = transformedText;
          newElement.value = transformedText;
        }

        return newElement;
      });
    }

    return options;
  };

  const handleValueSearchForm = ({ values }) => {
    if (optionHasChanged || (values.searchInput && Object.keys(values.searchInput.query).length <= 0)) {
      optionHasChanged = false;

      // eslint-disable-next-line no-param-reassign
      delete values.searchInput;
    }

    return values.searchInput;
  };

  const handleSearchInput = (item) => {
    setSearchInput({ propertyName: { value: item.searchInput.value } });
  };

  const handleSelectedDetail = (mainForm, values) => {
    mainForm.setValues({
      ...mainForm.values,
      requestDetail: values.searchInput.query,
      id: { ...mainForm.values.id, requestDetail: values.searchInput.value }
    });
  };

  const handleChangeOption = (mainForm, { target }, dataOptions) => {
    const result = dataOptions.filter(
      (item) => item.label.localeCompare(target.value, undefined, { sensitivity: 'base' }) === 0
    );

    selectedOption.current[stepId] = target.value;

    mainForm.setValues({
      ...mainForm.values,
      [target.name]: result[0].label,
      id: { ...mainForm.values.id, [target.name]: result[0].id }
    });

    if (stepId === 0) {
      // selected Request Connection has changed
      optionHasChanged = true;
    }
  };

  const handleChangeAttribute = (mainForm, { target }, elementId, formElement, numAttributes) => {
    const attributeId = elementId.replaceAll(/\s/g, '');

    const attributeValue = { value: target.value, formElement };
    if (
      formElement.localeCompare('Select', undefined, { sensitivity: 'base' }) === 0 &&
      Object.prototype.hasOwnProperty.call(target, 'label')
    ) {
      attributeValue.label = target.label;
    } else if (
      formElement.localeCompare('Auto', undefined, { sensitivity: 'base' }) === 0 &&
      Object.prototype.hasOwnProperty.call(target, 'complement')
    ) {
      attributeValue.complement = { name: target.complement.name, value: target.complement.value };
    }

    mainForm.setValues({
      ...mainForm.values,
      connectionAttributes: {
        ...mainForm.values.connectionAttributes,
        [attributeId]: attributeValue
      },
      id: {
        ...mainForm.values.id,
        connectionAttributes: { ...mainForm.values.id.connectionAttributes, [attributeId]: target.value }
      }
    });

    totalAttributes.current = numAttributes;
  };

  const handleEditAttributeAutoComplete = (mainForm, column) => {
    // eslint-disable-next-line no-param-reassign
    delete mainForm.values.connectionAttributes[column];

    // eslint-disable-next-line no-param-reassign
    delete mainForm.values.id.connectionAttributes[column];
  };

  const handleSetStep = (stepIndex, selectedTitle) => {
    setStepId(stepIndex);

    switch (stepIndex) {
      case 1:
      case 2:
      case 3:
        setMainTitle(`Request Connections to ${selectedTitle}`);
        break;

      case 4:
        setMainTitle('Summary of the Requested Connection');
        break;

      default:
        // step 0 - Initiate
        setMainTitle('What would you like to create a connection for?');
        break;
    }
  };

  const handleCloseMainModal = (confirmed) => {
    const step = parseInt(stepId, 10);

    // Check if small modal
    if (step > 0) {
      setOpenSmallModal(!openSmallModal);
      if (confirmed) {
        closeModal();
      }
    } else {
      closeModal();
    }
  };

  /* Submit connection to be created */
  const handleSubmitConnection = async (formDetails) => {
    const { id: argDetails } = formDetails;

    setIsSubmitting(!isSubmitting);

    const connectionInput = {
      // identityId: parseInt(admin.userId, 10)
      active: true,
      connectorId: argDetails.connectionProvider,
      entityTypeId: argDetails.requestConnection,
      entityId: parseInt(argDetails.requestDetail, 10),
      connectionTypeId: argDetails.connectionType
    };

    const attributes = argDetails.connectionAttributes;
    // Check for extra configuration
    if (attributes && Object.keys(attributes).length > 0) {
      connectionInput.chainCode = attributes.ChainCode;
      connectionInput.connectionPropertyCode = attributes.HotelCode;
      connectionInput.primaryRoomCode = attributes.RoomCodeType;
    }

    // Call mutation to create new connection
    await createConnectorConnection(connectionInput);
    closeModal();
  };

  return (
    <Formik
      initialValues={{
        id: {},
        requestConnection: '',
        requestDetail: '',
        connectionType: '',
        connectionProvider: '',
        connectionAttributes: {}
      }}
    >
      {(mainForm) => (
        <Modal
          disableEnforceFocus
          openModal
          handleClose={() => handleCloseMainModal(false)}
          title={stepId === 0 ? mainTitle : null}
        >
          {isSubmitting ? (
            <Row>
              <Grid.Col lg={12}>
                <div style={{ justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                  <Text.HeadingFourth align="center">Saving New Connection</Text.HeadingFourth>
                  <Spinner size={22} />
                </div>
              </Grid.Col>
            </Row>
          ) : (
            <Content>
              {stepId === 0 && (
                <ConnectorEntityContainer>
                  {({ data, error, loading }) => (
                    <Initiate
                      stepId={stepId}
                      mainForm={mainForm}
                      selectedOption={selectedOption.current}
                      searchInput={searchInput}
                      data={hydrateConnectionOptions(data.entityType)}
                      error={error.connectionError}
                      loading={loading.connectionLoading}
                      handleChangeOption={handleChangeOption}
                      handleValueSearchForm={handleValueSearchForm}
                      handleSearchInput={handleSearchInput}
                      handleSelectedDetail={handleSelectedDetail}
                      handleSetStep={handleSetStep}
                    />
                  )}
                </ConnectorEntityContainer>
              )}
              {stepId > 0 && (
                <NewConnection
                  stepId={stepId}
                  mainTitle={mainTitle}
                  mainForm={mainForm}
                  selectedOption={selectedOption.current}
                  totalAttributes={totalAttributes.current}
                  openSmallModal={openSmallModal}
                  handleChangeOption={handleChangeOption}
                  handleChangeAttribute={handleChangeAttribute}
                  handleSetStep={handleSetStep}
                  handleCloseMainModal={handleCloseMainModal}
                  handleSubmitConnection={handleSubmitConnection}
                  handleEditAttributeAutoComplete={handleEditAttributeAutoComplete}
                  hydrateConnectionOptions={hydrateConnectionOptions}
                />
              )}
            </Content>
          )}
        </Modal>
      )}
    </Formik>
  );
}

CreateConnectionsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  createConnectorConnection: PropTypes.func.isRequired,
  admin: PropTypes.shape({}).isRequired,
  router: PropTypes.shape({
    query: PropTypes.shape({
      propertyId: PropTypes.string,
      propertyName: PropTypes.string
    }).isRequired
  })
};

CreateConnectionsModal.defaultProps = {
  router: { query: {} }
};

export default CreateConnectionsModal;
